import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, User, Building, Globe, Lock, Gift, HelpCircle, MessageCircle } from 'lucide-react';
import LoginLeftSide from '../components/LoginLeftSide';
import styled from 'styled-components';
import moment from 'moment-timezone';

// Styled Components
const Container = styled.div`
  display: flex;
  min-height: 100vh;
  flex-direction: column;
  
  @media (min-width: 1024px) {
    flex-direction: row;
  }
`;

const RightSection = styled.div`
  width: 100%;
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  margin-left: 50%;
  min-height: 100vh;
  
  @media (max-width: 1024px) {
    margin-left: 0;
    width: 100%;
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const FormContainer = styled.div`
  width: 100%;
  max-width: 32rem;
  padding: 2rem;

  @media (max-width: 768px) {
    max-width: 100%;
    padding: 1.5rem;
  }
`;

const FormTitle = styled.h1`
  font-size: 1.875rem;
  font-weight: bold;
  color: #1f2937;
  margin-bottom: 0.75rem;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const FormSubtitle = styled.p`
  color: #000000;
  margin-bottom: 2.5rem;
  text-align: center;
  font-size: 1rem;

  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.5rem;
  }

  .password-mismatch {
    font-size: 0.75rem;
    color: #ef4444;
    margin-top: 0.25rem;
  }

  .coupon-section {
    margin-top: 0.5rem;
  }

  .coupon-input-container {
    display: flex;
    gap: 0.75rem;
  }

  .verify-coupon-btn {
    padding: 0 1.5rem;
    background-color: #1562B7;
    color: white;
    border: none;
    border-radius: 0.5rem;
    cursor: pointer;
    font-size: 0.875rem;
    min-width: 100px;
    
    &:hover {
      background-color: #0f529c;
    }
    
    &:disabled {
      background-color: #9ca3af;
      cursor: not-allowed;
    }
  }

  .coupon-message {
    font-size: 0.75rem;
    margin-top: 0.25rem;
    
    &.success {
      color: #10b981;
    }
    
    &.error {
      color: #ef4444;
    }
  }
`;

const InputWrapper = styled.div`
  position: relative;
  margin-bottom: 0.5rem;

  &.coupon-input {
    flex: 1;
    margin-bottom: 0;
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  display: flex;
  align-items: center;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 3rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 1rem;
  height: 3rem;

  &:focus {
    outline: none;
    border-color: transparent;
    box-shadow: 0 0 0 2px #1562B7;
  }
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 3rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 1rem;
  height: 3rem;
  background-color: white;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239ca3af'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1.5em 1.5em;

  &:focus {
    outline: none;
    border-color: transparent;
    box-shadow: 0 0 0 2px #1562B7;
  }
`;

const RequirementsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0.5rem 0 0;
`;

const RequirementItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  margin-bottom: 0.25rem;
  color: ${props => props.met ? '#10b981' : '#ef4444'};

  .requirement-icon {
    font-weight: bold;
  }
`;

const StyledButton = styled.button`
  width: 100%;
  background-color: #1562B7;
  color: #fff;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 0.5rem;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-top: 0.5rem;

  &:hover {
    background-color: #0f529c;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SignInPrompt = styled.div`
  margin-top: 2rem;
  text-align: center;
  font-size: 0.875rem;
  color: #4b5563;

  a {
    color: #1562B7;
    font-weight: 500;
    text-decoration: none;
    transition: color 0.3s;

    &:hover {
      color: #422d52;
    }
  }
`;

const SupportLinks = styled.div`
  margin-top: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  font-size: 0.875rem;
  color: #6b7280;

  a {
    display: flex;
    align-items: center;
    text-decoration: none;
    color: #6b7280;
    transition: color 0.3s;

    &:hover {
      color: #374151;
    }

    svg {
      margin-right: 0.25rem;
    }
  }
`;

const ErrorMessage = styled.div`
  margin: 1rem 0;
  padding: 0.75rem;
  border-radius: 0.375rem;
  background-color: #fee2e2;
  color: #b91c1c;
  text-align: center;
  font-size: 0.875rem;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 0.5rem;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 1rem;
  text-align: center;
`;

const ModalDescription = styled.p`
  margin-bottom: 1.5rem;
  color: #4b5563;
  text-align: center;
  font-size: 0.875rem;
`;

const OtpInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 1rem;
  letter-spacing: 0.1em;
  text-align: center;
  margin-bottom: 1.5rem;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
`;

const VerifyButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  background-color: #1562B7;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #0f529c;
  }

  &:disabled {
    background-color: #9ca3af;
    cursor: not-allowed;
  }
`;

const ResendButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  background-color: #1562B7;
  color: #ffffff;
  border: none;
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background-color: #1562B7;
  }

  &:disabled {
    background-color: #e5e7eb;
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const checkPasswordRequirements = (password) => {
  return {
    length: password.length >= 9,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };
};

function SignupPage() {
  const [formData, setFormData] = useState({
    name: '',
    businessName: '',
    timeZone: moment.tz.guess() || 'UTC',
    businessEmail: '',
    password: '',
    confirmPassword: '',
    couponCode: ''
  });

  const timezoneOptions = moment.tz.names();

  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });

  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [otp, setOtp] = useState('');
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponMessage, setCouponMessage] = useState('');

  useEffect(() => {
    setPasswordRequirements(checkPasswordRequirements(formData.password));
  }, [formData.password]);

  useEffect(() => {
    if (formData.confirmPassword) {
      setPasswordsMatch(formData.password === formData.confirmPassword);
    }
  }, [formData.password, formData.confirmPassword]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    if (name === 'couponCode') {
      setCouponApplied(false);
      setCouponMessage('');
    }
  };

  const isPasswordValid = () => {
    return Object.values(passwordRequirements).every(requirement => requirement) && passwordsMatch;
  };

  // const validateCouponCode = (code) => {
  //   const codeRegex = /^[A-Za-z0-9]{7}$/;
  //   return codeRegex.test(code);
  // };

  const handleVerifyCoupon = async (e) => {
    e.preventDefault();
  
    if (!formData.couponCode) {
      setCouponMessage('Please enter a coupon code');
      return;
    }
  
    // if (!validateCouponCode(formData.couponCode)) {
    //   setCouponMessage('Invalid coupon format. Must be 6 characters alphanumeric.');
    //   return;
    // }
  
    setLoading(true);
    setCouponMessage('');
  
    try {
      const response = await fetch("https://api.zerodayops.com/api/auth/verify-coupon", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ couponCode: formData.couponCode }),
      });
  
      const data = await response.json();
  
      if (response.ok && data.success) {
        setCouponApplied(true);
        setCouponMessage('Coupon applied successfully!');
      } else {
        setCouponMessage(data.message || 'Invalid coupon code');
      }
    } catch (err) {
      console.error(err);
      setCouponMessage('Failed to verify coupon code');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isPasswordValid()) {
      setMessage('Please ensure your password meets all requirements and passwords match');
      return;
    }

    setMessage('');
    setLoading(true);

    const payload = {
      name: formData.name,
      organizationName: formData.businessName,
      email: formData.businessEmail,
      password: formData.password,
      timeZone: formData.timeZone,
      couponCode: couponApplied ? formData.couponCode : undefined
    };

    try {
      const response = await fetch("https://api.zerodayops.com/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (response.ok) {
        setMessage(data.message || "OTP sent to your email. Please verify.");
        setShowOtpModal(true);
      } else {
        setMessage(data.message || "Registration failed.");
      }
    } catch (err) {
      console.error(err);
      setMessage("An error occurred during registration.");
    }
    setLoading(false);
  };

  const handleVerifyOtp = async () => {
    if (!otp) {
      setMessage('Please enter OTP');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch("https://api.zerodayops.com/api/auth/register-verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.businessEmail,
          otp: otp
        })
      });
      const data = await response.json();
      if (response.ok) {
        setMessage('Registration successful!');
        window.location.href = '/login';
      } else {
        setMessage(data.message || 'OTP verification failed');
      }
    } catch (err) {
      console.error(err);
      setMessage('An error occurred during OTP verification');
    }
    setLoading(false);
  };

  const handleResendOtp = async () => {
    setLoading(true);
    try {
      const response = await fetch("https://api.zerodayops.com/api/auth/register-resend-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.businessEmail
        })
      });
      const data = await response.json();
      if (response.ok) {
        setMessage('OTP resent successfully');
      } else {
        setMessage(data.message || 'Failed to resend OTP');
      }
    } catch (err) {
      console.error(err);
      setMessage('An error occurred while resending OTP');
    }
    setLoading(false);
  };

  return (
    <Container>
      <LoginLeftSide />
      <RightSection>
        <FormContainer>
          <FormTitle>Create Account</FormTitle>
          <FormSubtitle>Sign up to get started with Zeroday Ops</FormSubtitle>
          
          {message && <ErrorMessage>{message}</ErrorMessage>}
          
          <StyledForm onSubmit={handleSubmit}>
            <div>
              <label htmlFor="name">Full Name</label>
              <InputWrapper>
                <InputIcon>
                  <User size={20} />
                </InputIcon>
                <StyledInput
                  type="text"
                  id="name"
                  name="name"
                  placeholder="Enter your name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </InputWrapper>
            </div>
            
            <div>
              <label htmlFor="businessName">Business Name</label>
              <InputWrapper>
                <InputIcon>
                  <Building size={20} />
                </InputIcon>
                <StyledInput
                  type="text"
                  id="businessName"
                  name="businessName"
                  placeholder="Enter your business name"
                  value={formData.businessName}
                  onChange={handleChange}
                  required
                />
              </InputWrapper>
            </div>
            
            <div>
              <label htmlFor="timeZone">Time Zone</label>
              <InputWrapper>
                <InputIcon>
                  <Globe size={20} />
                </InputIcon>
                <StyledSelect
                  id="timeZone"
                  name="timeZone"
                  value={formData.timeZone}
                  onChange={handleChange}
                  required
                >
                  {timezoneOptions.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </StyledSelect>
              </InputWrapper>
            </div>
            
            <div>
              <label htmlFor="businessEmail">Business Email</label>
              <InputWrapper>
                <InputIcon>
                  <Mail size={20} />
                </InputIcon>
                <StyledInput
                  type="email"
                  id="businessEmail"
                  name="businessEmail"
                  placeholder="Enter your business email"
                  value={formData.businessEmail}
                  onChange={handleChange}
                  required
                />
              </InputWrapper>
            </div>
            
            <div>
              <label htmlFor="password">Password</label>
              <InputWrapper>
                <InputIcon>
                  <Lock size={20} />
                </InputIcon>
                <StyledInput
                  type="password"
                  id="password"
                  name="password"
                  placeholder="Create a secure password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
              </InputWrapper>
              <RequirementsList>
                {Object.entries(passwordRequirements).map(([key, met]) => (
                  <RequirementItem key={key} met={met}>
                    <span className="requirement-icon">{met ? '✓' : '×'}</span>
                    <span>
                      {key === 'length' && 'At least 9 characters'}
                      {key === 'uppercase' && 'One uppercase letter'}
                      {key === 'lowercase' && 'One lowercase letter'}
                      {key === 'number' && 'One number'}
                      {key === 'special' && 'One special character'}
                    </span>
                  </RequirementItem>
                ))}
              </RequirementsList>
            </div>
            
            <div>
              <label htmlFor="confirmPassword">Confirm Password</label>
              <InputWrapper>
                <InputIcon>
                  <Lock size={20} />
                </InputIcon>
                <StyledInput
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                />
              </InputWrapper>
              {formData.confirmPassword && !passwordsMatch && (
                <div className="password-mismatch">Passwords do not match</div>
              )}
            </div>
            
            <div className="coupon-section">
              <label htmlFor="couponCode">Coupon Code (Optional)</label>
              <div className="coupon-input-container">
                <InputWrapper className="coupon-input">
                  <InputIcon>
                    <Gift size={20} />
                  </InputIcon>
                  <StyledInput
                    type="text"
                    id="couponCode"
                    name="couponCode"
                    placeholder="Enter coupon code"
                    value={formData.couponCode}
                    onChange={handleChange}
                    disabled={couponApplied}
                  />
                </InputWrapper>
                <button 
                  type="button"
                  className="verify-coupon-btn"
                  onClick={handleVerifyCoupon}
                  disabled={!formData.couponCode || couponApplied || loading}
                >
                  {loading ? 'Verifying...' : 'Apply'}
                </button>
              </div>
              {couponMessage && (
                <div className={`coupon-message ${couponApplied ? 'success' : 'error'}`}>
                  {couponMessage}
                </div>
              )}
            </div>
            
            <StyledButton 
              type="submit" 
              disabled={loading || !isPasswordValid()}
            >
              {loading ? 'Processing...' : 'Create Account'}
            </StyledButton>
          </StyledForm>
          
          <SignInPrompt>
            Already have an account? <Link to="/login">Sign in</Link>
          </SignInPrompt>
          
          <SupportLinks>
            <a href="mailto:support@zerodayops.com">
              <HelpCircle size={16} /> Help
            </a>
            <a href="mailto:contact@zerodayops.com">
              <MessageCircle size={16} /> Contact Support
            </a>
          </SupportLinks>
        </FormContainer>
      </RightSection>

      {showOtpModal && (
        <ModalOverlay onClick={() => setShowOtpModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>OTP Verification</ModalTitle>
            <ModalDescription>
              A one-time password has been sent to <strong>{formData.businessEmail}</strong>. 
              Please enter the 6-digit code to verify your account.
            </ModalDescription>
            <OtpInput
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="Enter 6-digit OTP"
              maxLength={6}
            />
            <ModalActions>
              <VerifyButton
                onClick={handleVerifyOtp}
                disabled={loading || !otp}
              >
                {loading ? 'Verifying...' : 'Verify OTP'}
              </VerifyButton>
              <ResendButton
                onClick={handleResendOtp}
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Resend OTP'}
              </ResendButton>
            </ModalActions>
            {message && <ErrorMessage>{message}</ErrorMessage>}
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
}

export default SignupPage;